import type { Journey } from './journeyData';

export const NAMERICA_MIDEAST_JOURNEYS: Journey[] = [
// ── NORTH AMERICA ────────────────────────────────────────────────────────
{
  id: 52,
  slug: 'alaska-wilderness-wildlife',
  title: 'Alaska: Wilderness & Wildlife',
  region: 'North America',
  country: 'Alaska',
  cities: ['Anchorage', 'Denali', 'Kenai Fjords', 'Glacier Bay', 'Juneau', 'Ketchikan'],
  travelStyle: 'Small Group Journey',
  wayToTravel: 'Small Group Journeys',
  interests: ['Nature & Wildlife', 'Adventure', 'Photography'],
  months: ['June', 'July', 'August'],
  years: ['2025', '2026', '2027'],
  days: 12,
  groupSize: 'Limited to 16 guests',
  activityLevel: 'Moderate',
  price: 11800,
  priceLabel: 'From $11,800 pp',
  availability: 'Open',
  shortDescription: 'Alaska\'s last frontier — brown bears fishing for salmon, humpback whales, calving glaciers, and Denali\'s towering summit.',
  overview: 'Alaska is North America\'s last great wilderness — a land of staggering scale and extraordinary wildlife. This Worldway × A&K journey explores Denali National Park, the Kenai Fjords\' marine wildlife, Glacier Bay\'s calving glaciers, and the Inside Passage\'s temperate rainforest.',
  highlights: [
  'Denali National Park — North America\'s highest peak',
  'Brown bears fishing for salmon at Katmai',
  'Kenai Fjords — humpback whales and orca',
  'Glacier Bay — calving glaciers and sea otters',
  'Inside Passage — bald eagles and black bears',
  'Juneau — Mendenhall Glacier and whale watching'],

  itinerary: ['Anchorage', 'Denali', 'Kenai Fjords', 'Glacier Bay', 'Juneau', 'Ketchikan'],
  itineraryDays: [
  { day: 'Days 1–2', title: 'Anchorage', description: 'Arrive in Anchorage. Alaska Native Heritage Center and Chugach State Park.', accommodation: 'Anchorage Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Days 3–4', title: 'Denali National Park', description: 'Drive to Denali. Bus tour into the park — grizzly bears, moose, caribou, and Dall sheep.', accommodation: 'Denali Wilderness Lodge', meals: 'All meals' },
  { day: 'Days 5–6', title: 'Kenai Fjords', description: 'Drive to Seward. Full-day Kenai Fjords cruise — humpback whales, orca, sea otters, and puffins.', accommodation: 'Seward Boutique Hotel', meals: 'Breakfast daily' },
  { day: 'Days 7–8', title: 'Katmai Brown Bears', description: 'Fly to Katmai National Park. Watch brown bears fishing for sockeye salmon at Brooks Falls.', accommodation: 'Katmai Lodge', meals: 'All meals' },
  { day: 'Days 9–10', title: 'Glacier Bay', description: 'Fly to Glacier Bay. Cruise among calving tidewater glaciers. Sea kayaking among icebergs.', accommodation: 'Glacier Bay Lodge', meals: 'All meals' },
  { day: 'Days 11–12', title: 'Juneau & Ketchikan', description: 'Mendenhall Glacier walk. Whale watching in Frederick Sound. Ketchikan — totem poles and salmon hatchery.', accommodation: 'Juneau Boutique Hotel', meals: 'Breakfast daily' }],

  accommodation: 'Luxury hotels, wilderness lodges, and boutique hotels.',
  included: ['All accommodation', 'Breakfast daily', 'Selected meals', 'Internal flights', 'Expert guide', 'Kenai Fjords cruise', 'Glacier Bay cruise'],
  excluded: ['International flights', 'Travel insurance', 'Gratuities', 'Most lunches and dinners'],
  transport: 'Internal flights and private coach.',
  meals: 'Breakfast daily. All meals at Denali, Katmai, and Glacier Bay.',
  packing: ['Waterproof jacket', 'Warm layers', 'Hiking boots', 'Binoculars', 'Camera with telephoto lens', 'Insect repellent'],
  visa: 'Alaska is a US state — ESTA or US visa required for most nationalities.',
  weather: 'June–August is Alaska\'s summer — long days (up to 20 hours), temperatures 15–22°C. Mosquitoes are active.',
  bookingConditions: '25% deposit to confirm. Full payment 90 days before departure.',
  cancellationPolicy: '90+ days: deposit forfeited. 60–89 days: 50%. Under 60 days: 100%.',
  faqs: [
  { q: 'Is Denali usually visible?', a: 'Denali is only visible about 30% of the time due to cloud cover. We plan multiple viewing opportunities to maximise chances.' }],

  seo: { title: 'Alaska Wilderness & Wildlife | Worldway × A&K', description: '12-day Alaska journey through Denali, Kenai Fjords, Katmai brown bears, and Glacier Bay.', keywords: ['Alaska tour', 'Denali', 'Alaska bears', 'Glacier Bay', 'Alaska wildlife'] },
  image: "https://images.unsplash.com/photo-1588736683955-1a7f1d03e2c4",
  alt: 'Brown bears fishing for salmon in a rushing river in Katmai National Park Alaska',
  gallery: [
  { url: "https://images.unsplash.com/photo-1604904721822-1db3d58c2ffc", alt: 'Denali mountain reflected in a lake in Alaska' },
  { url: "https://images.unsplash.com/photo-1553336973-56c1cb69ce88", alt: 'Humpback whale breaching in the waters of Kenai Fjords Alaska' },
  { url: "https://images.unsplash.com/photo-1663127645421-7a17baa1dc75", alt: 'Calving glacier in Glacier Bay National Park Alaska' }],

  relatedSlugs: ['canada-rockies-pacific-coast', 'svalbard-polar-bear-safari', 'greenland-icebergs-inuit']
},
{
  id: 53,
  slug: 'canada-rockies-pacific-coast',
  title: 'Canada: Rockies & Pacific Coast',
  region: 'North America',
  country: 'Canada',
  cities: ['Vancouver', 'Whistler', 'Banff', 'Jasper', 'Calgary', 'Victoria'],
  travelStyle: 'Small Group Journey',
  wayToTravel: 'Small Group Journeys',
  interests: ['Nature & Wildlife', 'Adventure', 'Photography'],
  months: ['June', 'July', 'August', 'September'],
  years: ['2025', '2026', '2027'],
  days: 14,
  groupSize: 'Limited to 16 guests',
  activityLevel: 'Moderate',
  price: 10800,
  priceLabel: 'From $10,800 pp',
  availability: 'Open',
  shortDescription: 'Canada\'s Rocky Mountain grandeur — turquoise lakes, grizzly bears, the Icefields Parkway, and Vancouver\'s Pacific Coast.',
  overview: 'Canada\'s Rocky Mountains are among the world\'s most spectacular landscapes — a UNESCO World Heritage Site of turquoise glacial lakes, soaring peaks, and abundant wildlife. This Worldway × A&K journey combines Vancouver\'s cosmopolitan Pacific Coast with the iconic Icefields Parkway and Banff\'s mountain splendour.',
  highlights: [
  'Banff National Park — Lake Louise and Moraine Lake',
  'Icefields Parkway — one of the world\'s great scenic drives',
  'Columbia Icefield — walk on the Athabasca Glacier',
  'Jasper National Park — grizzly bears and elk',
  'Vancouver — Stanley Park, Granville Island, and Whistler',
  'Victoria — Butchart Gardens and whale watching'],

  itinerary: ['Vancouver', 'Whistler', 'Kamloops', 'Banff', 'Icefields Parkway', 'Jasper', 'Calgary'],
  itineraryDays: [
  { day: 'Days 1–3', title: 'Vancouver & Victoria', description: 'Arrive in Vancouver. Stanley Park, Granville Island, and Capilano Suspension Bridge. Ferry to Victoria — Butchart Gardens.', accommodation: 'Vancouver Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Days 4–5', title: 'Whistler', description: 'Drive to Whistler. Peak 2 Peak Gondola, mountain biking, and glacier skiing (seasonal).', accommodation: 'Whistler Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Days 6–8', title: 'Banff National Park', description: 'Drive to Banff. Lake Louise, Moraine Lake, and Johnston Canyon. Wildlife spotting — elk, black bear, and bighorn sheep.', accommodation: 'Banff Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Days 9–11', title: 'Icefields Parkway & Jasper', description: 'Drive the Icefields Parkway — the world\'s most scenic highway. Columbia Icefield walk. Jasper — grizzly bears and hot springs.', accommodation: 'Jasper Luxury Lodge', meals: 'Breakfast daily' },
  { day: 'Days 12–14', title: 'Calgary & Departure', description: 'Drive to Calgary. Heritage Park and Calgary Tower. Departure from Calgary Airport.', accommodation: 'Calgary Luxury Hotel', meals: 'Breakfast daily' }],

  accommodation: 'Luxury hotels and mountain lodges throughout.',
  included: ['All accommodation', 'Breakfast daily', 'Expert guide', 'Columbia Icefield walk', 'Peak 2 Peak Gondola', 'All transfers'],
  excluded: ['International flights', 'Travel insurance', 'Gratuities', 'Most lunches and dinners'],
  transport: 'Private coach throughout.',
  meals: 'Breakfast daily.',
  packing: ['Warm layers', 'Waterproof jacket', 'Hiking boots', 'Sunscreen', 'Binoculars', 'Camera'],
  visa: 'Canada eTA required for most nationalities.',
  weather: 'June–September is ideal — warm (15–25°C), wildflowers in July. September offers autumn colours.',
  bookingConditions: '25% deposit to confirm. Full payment 90 days before departure.',
  cancellationPolicy: '90+ days: deposit forfeited. 60–89 days: 50%. Under 60 days: 100%.',
  faqs: [
  { q: 'Is Moraine Lake accessible?', a: 'Moraine Lake requires a shuttle reservation in summer due to high demand. We arrange all reservations in advance.' }],

  seo: { title: 'Canada Rockies & Pacific Coast | Worldway × A&K', description: '14-day Canada journey through Vancouver, Banff, Icefields Parkway, and Jasper National Park.', keywords: ['Canada Rockies', 'Banff', 'Icefields Parkway', 'Jasper', 'Lake Louise'] },
  image: "https://images.unsplash.com/photo-1692176048915-b4bbc02ee4ff",
  alt: 'Turquoise Lake Louise reflecting snow-capped Rocky Mountains in Banff National Park Canada',
  gallery: [
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_173cb0272-1772537526708.png", alt: 'Moraine Lake with turquoise water and Valley of the Ten Peaks Banff Canada' },
  { url: "https://images.unsplash.com/photo-1730604261089-516a438a3fb7", alt: 'Grizzly bear in Jasper National Park Canada' },
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5aa996c-1785840267713.png", alt: 'Athabasca Glacier on the Columbia Icefield Icefields Parkway Canada' }],

  relatedSlugs: ['alaska-wilderness-wildlife', 'usa-national-parks-grand-canyon', 'new-zealand-north-south-islands']
},
{
  id: 54,
  slug: 'usa-national-parks-grand-canyon',
  title: 'USA: National Parks & Grand Canyon',
  region: 'North America',
  country: 'USA',
  cities: ['Las Vegas', 'Grand Canyon', 'Zion', 'Bryce Canyon', 'Arches', 'Monument Valley'],
  travelStyle: 'Small Group Journey',
  wayToTravel: 'Small Group Journeys',
  interests: ['Adventure', 'Nature & Wildlife', 'Photography'],
  months: ['March', 'April', 'May', 'September', 'October'],
  years: ['2025', '2026', '2027'],
  days: 10,
  groupSize: 'Limited to 16 guests',
  activityLevel: 'Moderate',
  price: 8400,
  priceLabel: 'From $8,400 pp',
  availability: 'Open',
  shortDescription: 'America\'s greatest national parks — the Grand Canyon, Zion\'s slot canyons, Bryce\'s hoodoos, and Monument Valley.',
  overview: 'The American Southwest contains some of the world\'s most dramatic landscapes. This Worldway × A&K journey explores the Grand Canyon\'s mile-deep gorge, Zion\'s towering sandstone walls, Bryce Canyon\'s otherworldly hoodoos, and Monument Valley\'s iconic buttes.',
  highlights: [
  'Grand Canyon — South Rim at sunrise and sunset',
  'Zion National Park — Angels Landing and The Narrows',
  'Bryce Canyon — hoodoo formations at dawn',
  'Arches National Park — Delicate Arch',
  'Monument Valley — Navajo Nation and iconic buttes',
  'Antelope Canyon — slot canyon light beams'],

  itinerary: ['Las Vegas', 'Zion', 'Bryce Canyon', 'Arches', 'Monument Valley', 'Grand Canyon', 'Las Vegas'],
  itineraryDays: [
  { day: 'Days 1–2', title: 'Las Vegas & Zion', description: 'Arrive in Las Vegas. Drive to Zion National Park. Angels Landing hike and Emerald Pools.', accommodation: 'Zion Luxury Lodge', meals: 'Breakfast daily' },
  { day: 'Day 3', title: 'Bryce Canyon', description: 'Drive to Bryce Canyon. Sunrise at Bryce Amphitheater — thousands of orange hoodoos.', accommodation: 'Bryce Canyon Lodge', meals: 'Breakfast' },
  { day: 'Days 4–5', title: 'Arches & Canyonlands', description: 'Drive to Moab. Arches National Park — Delicate Arch at sunset. Canyonlands overview.', accommodation: 'Moab Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Day 6', title: 'Monument Valley', description: 'Drive through Monument Valley — Navajo Nation guided tour of the iconic buttes.', accommodation: 'Monument Valley View Hotel', meals: 'Breakfast' },
  { day: 'Day 7', title: 'Antelope Canyon', description: 'Antelope Canyon — guided tour of the slot canyon with extraordinary light beams. Horseshoe Bend.', accommodation: 'Page Hotel', meals: 'Breakfast' },
  { day: 'Days 8–10', title: 'Grand Canyon', description: 'Grand Canyon South Rim — sunrise and sunset. Rim Trail and Desert View. Return to Las Vegas.', accommodation: 'Grand Canyon Luxury Hotel', meals: 'Breakfast daily' }],

  accommodation: 'Luxury lodges and hotels throughout.',
  included: ['All accommodation', 'Breakfast daily', 'Expert guide', 'All park fees', 'Navajo guide', 'Antelope Canyon tour'],
  excluded: ['International flights', 'Travel insurance', 'Gratuities', 'Most lunches and dinners'],
  transport: 'Private coach throughout.',
  meals: 'Breakfast daily.',
  packing: ['Comfortable hiking shoes', 'Sunscreen (high SPF)', 'Hat', 'Warm layers for canyon nights', 'Camera', 'Water bottle'],
  visa: 'USA ESTA or visa required for most nationalities.',
  weather: 'March–May and September–October are ideal — mild (15–25°C). Summer is extremely hot (40°C+) in the canyons.',
  bookingConditions: '25% deposit to confirm. Full payment 90 days before departure.',
  cancellationPolicy: '90+ days: deposit forfeited. 60–89 days: 50%. Under 60 days: 100%.',
  faqs: [
  { q: 'Is Angels Landing suitable for everyone?', a: 'Angels Landing involves chains and steep drops. We offer alternative hikes for those who prefer a less exposed route.' }],

  seo: { title: 'USA National Parks & Grand Canyon | Worldway × A&K', description: '10-day USA Southwest journey through Grand Canyon, Zion, Bryce Canyon, Arches, and Monument Valley.', keywords: ['USA national parks', 'Grand Canyon', 'Zion National Park', 'Bryce Canyon', 'Monument Valley'] },
  image: "https://images.unsplash.com/photo-1444726337012-5cf431682da3",
  alt: 'Grand Canyon South Rim at sunrise with layers of red and orange rock formations',
  gallery: [
  { url: "https://images.unsplash.com/photo-1709776681381-c8dd52565d2e", alt: 'Delicate Arch at sunset in Arches National Park Utah USA' },
  { url: "https://images.unsplash.com/photo-1474464123933-9553d590bc7c", alt: 'Hoodoo formations at Bryce Canyon National Park Utah USA' },
  { url: "https://images.unsplash.com/photo-1523617685608-c894fe8e9bed", alt: 'Monument Valley buttes at dawn with red desert landscape USA' }],

  relatedSlugs: ['canada-rockies-pacific-coast', 'alaska-wilderness-wildlife', 'mexico-ancient-civilisations']
},

// ── MIDDLE EAST ──────────────────────────────────────────────────────────
{
  id: 55,
  slug: 'jordan-petra-wadi-rum',
  title: 'Jordan: Petra & Wadi Rum',
  region: 'Middle East',
  country: 'Jordan',
  cities: ['Amman', 'Jerash', 'Petra', 'Wadi Rum', 'Aqaba', 'Dead Sea'],
  travelStyle: 'Small Group Journey',
  wayToTravel: 'Small Group Journeys',
  interests: ['History & Heritage', 'Adventure', 'Art & Culture'],
  months: ['March', 'April', 'October', 'November'],
  years: ['2025', '2026', '2027'],
  days: 9,
  groupSize: 'Limited to 16 guests',
  activityLevel: 'Moderate',
  price: 7400,
  priceLabel: 'From $7,400 pp',
  availability: 'Open',
  shortDescription: 'Jordan\'s ancient wonders — the rose-red city of Petra, Wadi Rum\'s Martian desert, and floating in the Dead Sea.',
  overview: 'Jordan is the Middle East\'s most accessible and rewarding destination. This Worldway × A&K journey explores the Nabataean city of Petra — one of the world\'s greatest archaeological sites — the otherworldly desert of Wadi Rum, and the therapeutic waters of the Dead Sea.',
  highlights: [
  'Petra — the Treasury at dawn before the crowds arrive',
  'Wadi Rum — overnight in a luxury Bedouin camp',
  'Dead Sea — float in the world\'s saltiest body of water',
  'Jerash — the best-preserved Roman city outside Italy',
  'Amman — Citadel and Roman Theatre',
  'Aqaba — Red Sea snorkelling'],

  itinerary: ['Amman', 'Jerash', 'Petra', 'Wadi Rum', 'Aqaba', 'Dead Sea', 'Amman'],
  itineraryDays: [
  { day: 'Days 1–2', title: 'Amman', description: 'Arrive in Amman. Citadel, Roman Theatre, and Jordan Museum. Welcome dinner.', accommodation: 'Amman Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Day 3', title: 'Jerash', description: 'Day trip to Jerash — the best-preserved Roman city outside Italy. Colonnaded street and oval plaza.', accommodation: 'Amman Luxury Hotel', meals: 'Breakfast' },
  { day: 'Days 4–5', title: 'Petra', description: 'Drive to Petra. Two days exploring the Nabataean city — Treasury, Monastery, and High Place of Sacrifice.', accommodation: 'Petra Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Days 6–7', title: 'Wadi Rum', description: 'Drive to Wadi Rum. 4WD desert safari. Overnight in a luxury Bedouin camp under the stars.', accommodation: 'Wadi Rum Luxury Camp', meals: 'All meals' },
  { day: 'Day 8', title: 'Aqaba', description: 'Drive to Aqaba. Red Sea snorkelling and seafood lunch.', accommodation: 'Aqaba Luxury Hotel', meals: 'Breakfast and lunch' },
  { day: 'Day 9', title: 'Dead Sea & Departure', description: 'Float in the Dead Sea. Mud treatment and spa. Return to Amman for departure.', accommodation: 'N/A', meals: 'Breakfast' }],

  accommodation: 'Luxury hotels, Petra hotel, and luxury Bedouin camp.',
  included: ['All accommodation', 'Breakfast daily', 'Wadi Rum all meals', 'Expert guide', 'Petra tickets', '4WD desert safari', 'All transfers'],
  excluded: ['International flights', 'Travel insurance', 'Gratuities', 'Jordan visa', 'Most lunches and dinners'],
  transport: 'Private air-conditioned coach throughout.',
  meals: 'Breakfast daily. All meals at Wadi Rum.',
  packing: ['Comfortable walking shoes', 'Modest clothing', 'Swimwear for Dead Sea and Aqaba', 'Sunscreen', 'Hat'],
  visa: 'Jordan visa on arrival or Jordan Pass (includes visa and Petra entry).',
  weather: 'March–April and October–November are ideal — mild (18–25°C). Summer is very hot (35–40°C).',
  bookingConditions: '25% deposit to confirm. Full payment 90 days before departure.',
  cancellationPolicy: '90+ days: deposit forfeited. 60–89 days: 50%. Under 60 days: 100%.',
  faqs: [
  { q: 'Is Petra accessible for those with mobility issues?', a: 'The main Siq and Treasury are accessible on foot or by horse-drawn carriage. The Monastery requires 800 steps.' }],

  seo: { title: 'Jordan Petra & Wadi Rum | Worldway × A&K', description: '9-day Jordan journey to Petra, Wadi Rum desert camp, Dead Sea, and Jerash Roman ruins.', keywords: ['Jordan tour', 'Petra', 'Wadi Rum', 'Dead Sea', 'Jerash'] },
  image: "https://images.unsplash.com/photo-1672485701908-9fd9b055fdb0",
  alt: 'The Treasury monument carved into rose-red sandstone cliffs in Petra Jordan',
  gallery: [
  { url: "https://images.unsplash.com/photo-1493930774055-e12c15dab2a7", alt: 'Wadi Rum red desert landscape with sandstone formations Jordan' },
  { url: "https://images.unsplash.com/photo-1629540249050-a58fea249675", alt: 'Tourists floating in the Dead Sea with salt formations Jordan' },
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_153015b94-1784478306182.png", alt: 'Colonnaded street at Jerash Roman ruins Jordan' }],

  relatedSlugs: ['oman-desert-wadis-muscat', 'saudi-arabia-nabataean-kingdoms', 'israel-holy-land-journey']
},
{
  id: 56,
  slug: 'oman-desert-wadis-muscat',
  title: 'Oman: Desert, Wadis & Muscat',
  region: 'Middle East',
  country: 'Oman',
  cities: ['Muscat', 'Nizwa', 'Wahiba Sands', 'Wadi Shab', 'Salalah'],
  travelStyle: 'Small Group Journey',
  wayToTravel: 'Small Group Journeys',
  interests: ['History & Heritage', 'Adventure', 'Art & Culture'],
  months: ['October', 'November', 'December', 'January', 'February', 'March'],
  years: ['2025', '2026', '2027'],
  days: 10,
  groupSize: 'Limited to 16 guests',
  activityLevel: 'Moderate',
  price: 8400,
  priceLabel: 'From $8,400 pp',
  availability: 'Open',
  shortDescription: 'Oman\'s hidden treasures — ancient forts, Bedouin desert camps, turquoise wadis, and the frankincense coast of Salalah.',
  overview: 'Oman is the Arabian Peninsula\'s most welcoming and authentic destination. This Worldway × A&K journey explores Muscat\'s grand Sultan Qaboos Mosque, the ancient forts of Nizwa, the golden dunes of Wahiba Sands, the turquoise pools of Wadi Shab, and the monsoon-green frankincense coast of Salalah.',
  highlights: [
  'Sultan Qaboos Grand Mosque — one of the world\'s most beautiful',
  'Nizwa Fort — the finest fort in Oman',
  'Wahiba Sands — overnight in a luxury desert camp',
  'Wadi Shab — turquoise pools and hidden waterfall',
  'Salalah — frankincense trees and monsoon landscape',
  'Mutrah Souq — the most authentic souq in Arabia'],

  itinerary: ['Muscat', 'Nizwa', 'Wahiba Sands', 'Wadi Shab', 'Sur', 'Salalah'],
  itineraryDays: [
  { day: 'Days 1–2', title: 'Muscat', description: 'Arrive in Muscat. Sultan Qaboos Grand Mosque, Mutrah Souq, and Royal Opera House.', accommodation: 'Muscat Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Days 3–4', title: 'Nizwa & Jebel Akhdar', description: 'Drive to Nizwa. Nizwa Fort and souq. Drive up to Jebel Akhdar — the Green Mountain.', accommodation: 'Nizwa Heritage Hotel', meals: 'Breakfast daily' },
  { day: 'Days 5–6', title: 'Wahiba Sands', description: 'Drive to Wahiba Sands. Dune bashing by 4WD. Overnight in a luxury Bedouin camp under the stars.', accommodation: 'Wahiba Sands Luxury Camp', meals: 'All meals' },
  { day: 'Day 7', title: 'Wadi Shab', description: 'Hike through Wadi Shab to the hidden turquoise pools and waterfall. Swim in the crystal-clear water.', accommodation: 'Sur Hotel', meals: 'Breakfast' },
  { day: 'Day 8', title: 'Sur & Turtle Beach', description: 'Sur dhow factory. Ras al-Jinz — watch green turtles nesting on the beach at night.', accommodation: 'Ras al-Jinz Turtle Reserve', meals: 'Breakfast and dinner' },
  { day: 'Days 9–10', title: 'Salalah & Departure', description: 'Fly to Salalah. Frankincense trees, Al-Baleed archaeological site, and Mughsail Beach. Return to Muscat.', accommodation: 'Salalah Luxury Hotel', meals: 'Breakfast daily' }],

  accommodation: 'Luxury hotels, heritage hotel, and luxury desert camp.',
  included: ['All accommodation', 'Breakfast daily', 'Wahiba Sands all meals', 'Expert guide', 'All entrance fees', 'Internal flight to Salalah'],
  excluded: ['International flights', 'Travel insurance', 'Gratuities', 'Oman visa', 'Most lunches and dinners'],
  transport: 'Private 4WD vehicles and internal flight.',
  meals: 'Breakfast daily. All meals at Wahiba Sands.',
  packing: ['Modest clothing', 'Comfortable walking shoes', 'Swimwear for wadis', 'Sunscreen', 'Warm layer for desert nights'],
  visa: 'Oman e-Visa required for most nationalities.',
  weather: 'October–March is ideal — mild (20–28°C). Salalah is best July–September during the khareef (monsoon).',
  bookingConditions: '25% deposit to confirm. Full payment 90 days before departure.',
  cancellationPolicy: '90+ days: deposit forfeited. 60–89 days: 50%. Under 60 days: 100%.',
  faqs: [
  { q: 'Is Oman safe for tourists?', a: 'Oman is one of the safest countries in the Middle East and consistently ranked among the world\'s most welcoming destinations.' }],

  seo: { title: 'Oman Desert Wadis & Muscat | Worldway × A&K', description: '10-day Oman journey through Muscat, Nizwa forts, Wahiba Sands desert camp, and Wadi Shab.', keywords: ['Oman tour', 'Wahiba Sands', 'Wadi Shab', 'Nizwa Fort', 'Muscat'] },
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a1e06246-1773164342930.png",
  alt: 'Ancient mud-brick fort rising above the date palm oasis of Nizwa in Oman',
  gallery: [
  { url: "https://images.unsplash.com/photo-1696834102503-9e6ad0b106a7", alt: 'Sultan Qaboos Grand Mosque with blue dome and minarets in Muscat Oman' },
  { url: "https://images.unsplash.com/photo-1579783447342-a64a25d6481b", alt: 'Turquoise pools in Wadi Shab canyon Oman' },
  { url: "https://images.unsplash.com/photo-1618059647436-f345e882fa48", alt: 'Golden sand dunes of Wahiba Sands at sunset Oman' }],

  relatedSlugs: ['jordan-petra-wadi-rum', 'saudi-arabia-nabataean-kingdoms', 'uae-dubai-abu-dhabi']
},
{
  id: 57,
  slug: 'saudi-arabia-nabataean-kingdoms',
  title: 'Saudi Arabia: Ancient Nabataean Kingdoms',
  region: 'Middle East',
  country: 'Saudi Arabia',
  cities: ['Riyadh', 'AlUla', 'Hegra', 'Diriyah', 'Jeddah'],
  travelStyle: 'Small Group Journey',
  wayToTravel: 'Small Group Journeys',
  interests: ['History & Heritage', 'Art & Culture', 'Adventure'],
  months: ['October', 'November', 'December', 'January', 'February', 'March'],
  years: ['2025', '2026', '2027'],
  days: 10,
  groupSize: 'Limited to 16 guests',
  activityLevel: 'Easy',
  price: 9600,
  priceLabel: 'From $9,600 pp',
  availability: 'Open',
  isNew: true,
  shortDescription: 'Saudi Arabia\'s extraordinary new tourism frontier — Hegra\'s Nabataean tombs, AlUla\'s sandstone landscapes, and Diriyah\'s UNESCO heritage.',
  overview: 'Saudi Arabia has opened its doors to international tourism, revealing extraordinary archaeological treasures that rival Petra. This Worldway × A&K journey explores Hegra — the Nabataean civilisation\'s second city — the dramatic sandstone landscapes of AlUla, the UNESCO heritage site of Diriyah, and the Red Sea coast of Jeddah.',
  highlights: [
  'Hegra (Mada\'in Saleh) — Nabataean tombs carved into sandstone',
  'AlUla — dramatic sandstone formations and ancient rock art',
  'Elephant Rock — a natural sandstone arch',
  'Diriyah — UNESCO birthplace of the Saudi state',
  'Jeddah Old Town — UNESCO coral-built historic district',
  'Red Sea — snorkelling in pristine coral reefs'],

  itinerary: ['Riyadh', 'Diriyah', 'AlUla', 'Hegra', 'Jeddah'],
  itineraryDays: [
  { day: 'Days 1–2', title: 'Riyadh', description: 'Arrive in Riyadh. National Museum, Kingdom Tower, and Masmak Fortress.', accommodation: 'Riyadh Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Day 3', title: 'Diriyah', description: 'Visit Diriyah — the UNESCO-listed birthplace of the Saudi state. At-Turaif District mud-brick palaces.', accommodation: 'Riyadh Luxury Hotel', meals: 'Breakfast' },
  { day: 'Days 4–7', title: 'AlUla & Hegra', description: 'Fly to AlUla. Explore the dramatic sandstone landscape, Elephant Rock, and Hegra\'s Nabataean tombs.', accommodation: 'AlUla Luxury Resort', meals: 'All meals' },
  { day: 'Days 8–10', title: 'Jeddah & Red Sea', description: 'Fly to Jeddah. Al-Balad UNESCO historic district, floating mosque, and Red Sea snorkelling.', accommodation: 'Jeddah Luxury Hotel', meals: 'Breakfast daily' }],

  accommodation: 'Luxury hotels and AlUla luxury resort.',
  included: ['All accommodation', 'Breakfast daily', 'AlUla all meals', 'Internal flights', 'Expert guide', 'All entrance fees'],
  excluded: ['International flights', 'Travel insurance', 'Gratuities', 'Saudi visa', 'Most lunches and dinners'],
  transport: 'Private coach and internal flights.',
  meals: 'Breakfast daily. All meals at AlUla.',
  packing: ['Modest clothing (shoulders and knees covered)', 'Comfortable walking shoes', 'Sunscreen', 'Hat', 'Swimwear for Red Sea'],
  visa: 'Saudi Arabia tourist e-Visa available for most nationalities.',
  weather: 'October–March is ideal — mild (15–25°C). Summer is extremely hot (40°C+).',
  bookingConditions: '25% deposit to confirm. Full payment 90 days before departure.',
  cancellationPolicy: '90+ days: deposit forfeited. 60–89 days: 50%. Under 60 days: 100%.',
  faqs: [
  { q: 'Is Saudi Arabia safe for tourists?', a: 'Saudi Arabia has invested heavily in tourism infrastructure and is safe for international visitors. Our guides ensure all cultural protocols are respected.' }],

  seo: { title: 'Saudi Arabia Nabataean Kingdoms | Worldway × A&K', description: '10-day Saudi Arabia journey to Hegra Nabataean tombs, AlUla sandstone landscapes, and Jeddah Red Sea.', keywords: ['Saudi Arabia tour', 'Hegra', 'AlUla', 'Nabataean tombs', 'Saudi tourism'] },
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_180add312-1772191573340.png",
  alt: 'Nabataean tombs carved into sandstone cliffs at Hegra ancient city in Saudi Arabia',
  gallery: [
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_11424ddb3-1772568886469.png", alt: 'Elephant Rock natural sandstone arch in AlUla Saudi Arabia' },
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1086d95e3-1772194397461.png", alt: 'Diriyah mud-brick palaces UNESCO heritage site Saudi Arabia' },
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1924aeda8-1778875932295.png", alt: 'Jeddah Al-Balad historic district coral buildings Saudi Arabia' }],

  relatedSlugs: ['jordan-petra-wadi-rum', 'oman-desert-wadis-muscat', 'uae-dubai-abu-dhabi']
},
{
  id: 58,
  slug: 'uae-dubai-abu-dhabi',
  title: 'UAE: Dubai & Abu Dhabi',
  region: 'Middle East',
  country: 'UAE',
  cities: ['Dubai', 'Abu Dhabi', 'Al Ain', 'Liwa Oasis'],
  travelStyle: 'Private Journey',
  wayToTravel: 'Private Safaris',
  interests: ['Luxury', 'History & Heritage', 'Art & Culture'],
  months: ['October', 'November', 'December', 'January', 'February', 'March'],
  years: ['2025', '2026', '2027'],
  days: 7,
  groupSize: 'Up to 8 guests',
  activityLevel: 'Easy',
  price: 12400,
  priceLabel: 'From $12,400 pp',
  availability: 'Open',
  shortDescription: 'The UAE\'s extraordinary contrasts — Dubai\'s futuristic skyline, Abu Dhabi\'s Sheikh Zayed Mosque, and the Empty Quarter desert.',
  overview: 'The UAE offers a unique blend of ultra-modern architecture, ancient Bedouin culture, and world-class luxury. This Worldway × A&K private journey explores Dubai\'s iconic skyline, Abu Dhabi\'s magnificent Sheikh Zayed Mosque, the ancient oasis city of Al Ain, and the vast dunes of the Liwa Oasis on the edge of the Empty Quarter.',
  highlights: [
  'Burj Khalifa — the world\'s tallest building',
  'Sheikh Zayed Grand Mosque — one of the world\'s most beautiful',
  'Liwa Oasis — the edge of the Empty Quarter desert',
  'Al Ain — UNESCO oasis city and camel market',
  'Dubai Creek — traditional abra boat and gold souq',
  'Louvre Abu Dhabi — world-class art museum'],

  itinerary: ['Dubai', 'Abu Dhabi', 'Al Ain', 'Liwa Oasis', 'Dubai'],
  itineraryDays: [
  { day: 'Days 1–3', title: 'Dubai', description: 'Arrive in Dubai. Burj Khalifa, Dubai Mall, Dubai Creek, and gold souq. Desert safari with dune bashing.', accommodation: 'Dubai Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Days 4–5', title: 'Abu Dhabi', description: 'Drive to Abu Dhabi. Sheikh Zayed Grand Mosque, Louvre Abu Dhabi, and Yas Island.', accommodation: 'Abu Dhabi Luxury Hotel', meals: 'Breakfast daily' },
  { day: 'Day 6', title: 'Al Ain & Liwa', description: 'Drive to Al Ain — UNESCO oasis city. Camel market and Jahili Fort. Continue to Liwa Oasis.', accommodation: 'Liwa Desert Resort', meals: 'All meals' },
  { day: 'Day 7', title: 'Return to Dubai', description: 'Morning dune walk in the Empty Quarter. Return to Dubai for departure.', accommodation: 'N/A', meals: 'Breakfast' }],

  accommodation: 'Luxury hotels in Dubai and Abu Dhabi, desert resort at Liwa.',
  included: ['All accommodation', 'Breakfast daily', 'Liwa all meals', 'Expert guide', 'Desert safari', 'All entrance fees'],
  excluded: ['International flights', 'Travel insurance', 'Gratuities', 'Most lunches and dinners'],
  transport: 'Private 4WD vehicles throughout.',
  meals: 'Breakfast daily. All meals at Liwa.',
  packing: ['Modest clothing for mosques', 'Comfortable walking shoes', 'Sunscreen', 'Swimwear for hotel pools', 'Smart casual for evenings'],
  visa: 'UAE offers visa-free entry for most Western passport holders.',
  weather: 'October–March is ideal — mild (20–28°C). Summer is extremely hot and humid (40°C+).',
  bookingConditions: '25% deposit to confirm. Full payment 90 days before departure.',
  cancellationPolicy: '90+ days: deposit forfeited. 60–89 days: 50%. Under 60 days: 100%.',
  faqs: [
  { q: 'Is Dubai suitable for families?', a: 'Yes, Dubai is very family-friendly with world-class theme parks, beaches, and activities for all ages.' }],

  seo: { title: 'UAE Dubai & Abu Dhabi | Worldway × A&K', description: '7-day UAE private journey through Dubai, Abu Dhabi Sheikh Zayed Mosque, and the Liwa Desert.', keywords: ['UAE tour', 'Dubai', 'Abu Dhabi', 'Sheikh Zayed Mosque', 'Liwa Desert'] },
  image: "https://images.unsplash.com/photo-1702109768051-473fa42beeb4",
  alt: 'Dubai skyline with Burj Khalifa and modern skyscrapers reflected in the water UAE',
  gallery: [
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad80f71e-1772891262339.png", alt: 'Sheikh Zayed Grand Mosque with white domes and minarets in Abu Dhabi UAE' },
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_17c1e1205-1778928807273.png", alt: 'Louvre Abu Dhabi museum with geometric dome UAE' },
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_10ad66551-1774553963878.png", alt: 'Liwa Oasis sand dunes on the edge of the Empty Quarter UAE' }],

  relatedSlugs: ['oman-desert-wadis-muscat', 'jordan-petra-wadi-rum', 'saudi-arabia-nabataean-kingdoms']
}];